<template>
  <div v-if="loading" class="text-center py-16">
    <v-progress-circular indeterminate color="#f59e0b" size="56" width="4" />
    <div class="text-body-2 text-medium-emphasis mt-4">Loading comic details...</div>
  </div>

  <v-alert v-else-if="error" type="error" class="my-6" variant="tonal">{{ error }}</v-alert>

  <div v-else-if="comic" class="comic-detail-view">
    <!-- Atmospheric Backdrop Hero -->
    <div class="hero-backdrop-container">
      <div
        class="hero-backdrop-image"
        :style="{ backgroundImage: `url(${posterSrc(comic)})` }"
      ></div>
      <div class="hero-backdrop-overlay"></div>
    </div>

    <!-- Navigation Bar -->
    <div class="d-flex align-center justify-space-between mb-6 position-relative z-index-2">
      <div class="d-flex align-center ga-2">
        <v-btn
          to="/comics/homeview"
          variant="tonal"
          size="small"
          class="btn-modern"
          prepend-icon="mdi-arrow-left"
        >
          Back to Comics
        </v-btn>
        <v-btn
          v-if="comic.serie_id != null"
          :to="{ name: 'ComicSerieDetail', params: { serieId: comic.serie_id } }"
          variant="tonal"
          size="small"
          class="btn-modern"
          prepend-icon="mdi-book-multiple-outline"
        >
          {{ comic.serie || 'View Series' }}
        </v-btn>
      </div>
    </div>

    <!-- Hero Content Card -->
    <div class="hero-content-card glass-card pa-6 pa-md-8 mb-8 position-relative z-index-2">
      <v-row align="start">
        <!-- Poster Column -->
        <v-col cols="12" sm="5" md="4" lg="3" class="d-flex justify-center justify-sm-start">
          <div class="poster-3d-wrapper">
            <v-img
              :src="posterSrc(comic)"
              :aspect-ratio="2 / 3"
              cover
              class="poster-3d-image rounded-xl"
            >
              <template #placeholder>
                <div class="poster-3d-placeholder">
                  <v-icon size="64" color="#f59e0b">mdi-draw</v-icon>
                </div>
              </template>
            </v-img>
            <div v-if="comic.issue_number || comic.tome" class="poster-issue-badge">
              {{ comic.tome ? `Tome ${comic.tome}` : `#${comic.issue_number}` }}
            </div>
          </div>
        </v-col>

        <!-- Metadata & Actions Column -->
        <v-col cols="12" sm="7" md="8" lg="9">
          <div class="d-flex flex-column h-100 justify-space-between">
            <div>
              <!-- Genre & Status Badges -->
              <div class="d-flex flex-wrap align-center ga-2 mb-3">
                <v-chip v-if="comic.category" color="#f59e0b" variant="flat" size="small" class="text-white font-weight-bold">
                  {{ comic.category }}
                </v-chip>
                <v-chip
                  v-if="comic.serie"
                  size="small"
                  variant="tonal"
                  color="#f59e0b"
                  class="font-weight-medium"
                >
                  <v-icon start size="14">mdi-book-multiple</v-icon>
                  {{ comic.serie }}
                </v-chip>
                <v-chip v-if="comic.tome" size="small" variant="tonal" class="font-weight-medium">
                  Tome {{ comic.tome }}
                </v-chip>
                <v-chip v-if="comic.year" size="small" variant="tonal" class="font-weight-medium">
                  {{ comic.year }}
                </v-chip>
                <v-chip v-if="comic.publisher" size="small" variant="tonal" class="font-weight-medium">
                  {{ comic.publisher }}
                </v-chip>
              </div>

              <!-- Title & Author / Artist -->
              <h1 class="comic-detail-title mb-1">{{ comic.title }}</h1>
              <div v-if="comic.author || comic.artist" class="text-subtitle-1 text-medium-emphasis mb-2">
                <span v-if="comic.author">By <strong class="text-high-emphasis">{{ comic.author }}</strong></span>
                <span v-if="comic.author && comic.artist"> • </span>
                <span v-if="comic.artist">Art by <strong class="text-high-emphasis">{{ comic.artist }}</strong></span>
              </div>
              <p v-if="comic.original && comic.original !== comic.title" class="text-caption text-medium-emphasis mb-4">
                <em>Original: {{ comic.original }}</em>
              </p>

              <!-- Rating -->
              <div v-if="comic.rating && Number(comic.rating) > 0" class="rating-display-box d-flex align-center ga-3 mb-6">
                <v-rating
                  :model-value="Number(comic.rating)"
                  half-increments
                  readonly
                  density="compact"
                  color="amber"
                  active-color="amber"
                />
                <span class="text-subtitle-1 font-weight-bold">{{ comic.rating }} / 5</span>
              </div>
            </div>

            <!-- Action Toolbar -->
            <div class="action-toolbar d-flex flex-wrap align-center ga-2 pt-4 border-t-subtle">
              <!-- Wishlist Toggle -->
              <v-btn
                v-if="isWishList"
                color="#f59e0b"
                variant="flat"
                class="btn-modern text-white font-weight-bold"
                prepend-icon="mdi-check-circle"
                :loading="movingToCollection"
                @click="moveToCollection"
              >
                Move to Owned
              </v-btn>

              <!-- Favorite Toggle -->
              <v-btn
                variant="tonal"
                :color="isFavorite ? 'amber-darken-2' : undefined"
                class="btn-modern"
                :prepend-icon="isFavorite ? 'mdi-heart' : 'mdi-heart-outline'"
                :loading="togglingFavorite"
                @click="toggleFavorite"
              >
                {{ isFavorite ? 'Favorited' : 'Favorite' }}
              </v-btn>

              <!-- Edit Post -->
              <v-btn
                :to="{ name: 'EditComicPost', params: { id: comic.id } }"
                variant="tonal"
                class="btn-modern"
                prepend-icon="mdi-pencil-outline"
              >
                Edit
              </v-btn>

              <v-spacer></v-spacer>

              <!-- Delete Action -->
              <v-btn
                color="error"
                variant="tonal"
                class="btn-modern"
                prepend-icon="mdi-delete-outline"
                :loading="deleting"
                @click="remove"
              >
                Delete
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Technical Specs Quick Grid -->
    <v-row class="mb-6" dense>
      <v-col v-if="comic.author" cols="6" sm="4" md="2">
        <v-card class="glass-card pa-4 text-center h-100">
          <div class="spec-label">Author</div>
          <div class="spec-value text-truncate">{{ comic.author }}</div>
        </v-card>
      </v-col>
      <v-col v-if="comic.serie" cols="6" sm="4" md="2">
        <v-card class="glass-card pa-4 text-center h-100">
          <div class="spec-label">Series</div>
          <div class="spec-value text-truncate">{{ comic.serie }}</div>
        </v-card>
      </v-col>
      <v-col v-if="comic.publisher" cols="6" sm="4" md="2">
        <v-card class="glass-card pa-4 text-center h-100">
          <div class="spec-label">Publisher</div>
          <div class="spec-value text-truncate">{{ comic.publisher }}</div>
        </v-card>
      </v-col>
      <v-col v-if="comic.year || comic.publishedDate" cols="6" sm="4" md="2">
        <v-card class="glass-card pa-4 text-center h-100">
          <div class="spec-label">Published</div>
          <div class="spec-value">{{ comic.publishedDate || comic.year }}</div>
        </v-card>
      </v-col>
      <v-col v-if="comic.pageCount" cols="6" sm="4" md="2">
        <v-card class="glass-card pa-4 text-center h-100">
          <div class="spec-label">Pages</div>
          <div class="spec-value">{{ comic.pageCount }}</div>
        </v-card>
      </v-col>
      <v-col v-if="comic.created" cols="6" sm="4" md="2">
        <v-card class="glass-card pa-4 text-center h-100">
          <div class="spec-label">Date Added</div>
          <div class="spec-value">{{ formatDate(comic.created) }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Narrative Section -->
    <Synopsis :comic="comic" />
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Synopsis from '@/components/comics/Synopsis.vue'
import ComicsAPI from '@/services/comics'
import { posterSrc } from '@/utils/comicForm'

export default {
  name: 'ComicDetail',
  components: { Synopsis },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const comic = ref(null)
    const loading = ref(true)
    const error = ref('')
    const deleting = ref(false)
    const togglingFavorite = ref(false)
    const movingToCollection = ref(false)

    const postMarked = (value) => {
      if (value == null || value === '') return '0'
      return String(value)
    }

    const isWishList = computed(() => postMarked(comic.value?.marked) === '2')
    const isFavorite = computed(() => postMarked(comic.value?.marked) === '1')

    const load = async () => {
      loading.value = true
      error.value = ''
      try {
        const { data } = await ComicsAPI.getPostByID(route.params.id)
        comic.value = data
      } catch (err) {
        error.value = err.response?.data?.detail?.message || 'Comic not found'
      } finally {
        loading.value = false
      }
    }

    const remove = async () => {
      if (!confirm(`Delete "${comic.value?.title || 'this comic'}" from your library?`)) return
      deleting.value = true
      try {
        await ComicsAPI.deletePost(route.params.id)
        router.push({ name: 'ComicsHome', query: { message: 'Comic deleted.' } })
      } catch (err) {
        error.value = err.response?.data?.detail?.message || 'Delete failed'
      } finally {
        deleting.value = false
      }
    }

    const toggleFavorite = async () => {
      togglingFavorite.value = true
      try {
        const { data } = await ComicsAPI.toggleFavorite(route.params.id)
        comic.value = data
      } catch (err) {
        error.value = err.response?.data?.detail?.message || 'Failed to update favorite'
      } finally {
        togglingFavorite.value = false
      }
    }

    const moveToCollection = async () => {
      movingToCollection.value = true
      try {
        const { data } = await ComicsAPI.moveToCollection(route.params.id)
        comic.value = data
      } catch (err) {
        error.value = err.response?.data?.detail?.message || 'Failed to move to collection'
      } finally {
        movingToCollection.value = false
      }
    }

    const formatDate = (value) => {
      if (!value) return ''
      return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    }

    onMounted(load)

    return {
      comic,
      loading,
      error,
      deleting,
      togglingFavorite,
      movingToCollection,
      isWishList,
      isFavorite,
      remove,
      toggleFavorite,
      moveToCollection,
      posterSrc,
      formatDate,
    }
  },
}
</script>

<style scoped>
.comic-detail-view {
  max-width: 1240px;
  margin: 0 auto;
  position: relative;
}

/* Atmospheric Backdrop */
.hero-backdrop-container {
  position: absolute;
  top: -40px;
  left: -200px;
  right: -200px;
  height: 480px;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}

.hero-backdrop-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center 25%;
  filter: blur(48px);
  opacity: 0.35;
  transform: scale(1.15);
}

.hero-backdrop-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at center, transparent 0%, rgba(10, 14, 26, 0.95) 100%);
}

.z-index-2 {
  z-index: 2;
}

/* Poster 3D Presentation */
.poster-3d-wrapper {
  position: relative;
  width: 100%;
  max-width: 260px;
}

.poster-3d-image {
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(245, 158, 11, 0.3);
}

.poster-3d-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 360px;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(251, 191, 36, 0.15));
}

.poster-issue-badge {
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: rgba(245, 158, 11, 0.9);
  backdrop-filter: blur(8px);
  color: #000;
  font-size: 0.75rem;
  font-weight: 800;
  padding: 4px 8px;
  border-radius: 6px;
}

/* Titles */
.comic-detail-title {
  font-size: clamp(1.75rem, 3.5vw, 2.75rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.02em;
}

.border-t-subtle {
  border-top: 1px solid rgba(128, 128, 128, 0.12);
}

/* Spec Box */
.spec-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(128, 128, 128, 0.8);
  margin-bottom: 4px;
}

.spec-value {
  font-size: 0.95rem;
  font-weight: 600;
}
</style>

